/**
 * Client-side currency helper.
 *
 * We persist the visitor's currency choice in a year-long `eb_currency`
 * cookie (see /api/pricing/currency). Server-side rendering reads it
 * before the page paints (no flash). Client-side checkout calls need
 * to be able to read it too — that's what this helper is for.
 *
 * The cookie is intentionally non-HttpOnly because of this single
 * read-site. We don't store anything sensitive in it.
 */
import { isSupportedCurrency, CURRENCY_COOKIE, type Currency } from './currency';

/**
 * Returns the visitor's preferred currency by reading the `eb_currency`
 * cookie. Returns `undefined` when:
 *   - Running on the server (no `document`)
 *   - Cookie absent
 *   - Cookie value is corrupted / not in our supported set
 *
 * Callers pass the result through as `body.currency` on checkout POSTs;
 * the server falls back to its own header-based resolver if the field
 * is absent, so `undefined` is always safe.
 */
export function readCurrencyCookie(): Currency | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${CURRENCY_COOKIE}=([^;]+)`),
  );
  if (!match) return undefined;
  const value = decodeURIComponent(match[1]);
  return isSupportedCurrency(value) ? value : undefined;
}
